import { useMemo, useState } from 'react';
import { calcDamage } from '../../calc/calc-damage.ts';
import { NATURE_LABEL } from '../util/champions-labels.ts';
import type { DamageMatrix, DamageMatrixRow, SpeciesId } from '../types.ts';
import { Sprite } from './Sprite.tsx';

// All canonical Stat Alignments (still internally called "natures" — see
// champions-labels.ts) we expose in dropdowns. Speed-modifying ones are
// included for the attacker side (Jolly/Adamant tradeoff is meta-defining)
// but defender-side dropdowns filter to Speed-neutral options.
// Hardy is the only neutral that survived the Champions cull; Docile / Bashful
// / Quirky were dropped from the game so we don't list them. Serious is the
// in-game-canonical neutral and is included too.
const NATURE_ALL = [
  'Serious',
  'Adamant',
  'Jolly',
  'Modest',
  'Timid',
  'Bold',
  'Calm',
  'Impish',
  'Careful',
  'Brave',
  'Quiet',
] as const;
const NATURE_DEFENDER = [
  'Serious',
  'Bold',
  'Calm',
  'Impish',
  'Careful',
  'Adamant',
  'Modest',
  'Jolly',
  'Timid',
] as const;

// Defender items that meaningfully change calc math. Assault Vest (1.5x SpD)
// and Eviolite (1.5x Def + SpD on NFEs) are the only true calc-movers; the
// rest are included so the user can toggle off a modal item ("what if not
// Sash?") or model a survival proc (Sitrus). Choice items are offensive and
// irrelevant on a defender — omitted.
const ITEM_CANONICAL = [
  'Assault Vest',
  'Eviolite',
  'Focus Sash',
  'Sitrus Berry',
  'Leftovers',
  'Rocky Helmet',
] as const;
const NO_ITEM = '(none)';

// Pikalytics stores items as lowercase concatenated strings ("assaultvest").
// Normalize to that form for equality checks against canonical names; render
// pretty names back in the dropdown.
function normalizeItem(s: string | undefined): string {
  return (s ?? '').toLowerCase().replace(/\s+/g, '');
}
function prettyItem(s: string | undefined): string {
  if (!s) return NO_ITEM;
  const target = normalizeItem(s);
  for (const canon of ITEM_CANONICAL) {
    if (normalizeItem(canon) === target) return canon;
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
}

type EvSet = { hp?: number; atk?: number; def?: number; spa?: number; spd?: number; spe?: number };
type DefenderOverride = {
  nature?: string;
  item?: string | null; // null means "(none)"
  evs?: EvSet;
};
type AttackerOverride = {
  nature?: string;
  evs?: EvSet;
  atk_stage?: number;
  spa_stage?: number;
};

export function DamageMatrixCard({ data }: { data: DamageMatrix }) {
  // All hooks must run unconditionally before any early return.
  const [attackerOv, setAttackerOv] = useState<AttackerOverride>({});
  const [defenderOv, setDefenderOv] = useState<Record<string, DefenderOverride>>({});

  const baseAttacker = useMemo(
    () => ({
      species: data?.attacker?.species ?? '',
      item: data?.attacker?.item,
      ability: data?.attacker?.ability,
      nature: data?.attacker?.nature ?? 'Hardy',
      level: 50,
      evs: (data?.attacker?.evs as EvSet) ?? {},
      ivs: data?.attacker?.ivs,
    }),
    [data?.attacker],
  );

  if (!data?.attacker?.species || !data.move || !Array.isArray(data.results)) {
    return (
      <div className="mono" style={{ color: 'var(--fg-4)', fontSize: 11, opacity: 0.6 }}>
        damage matrix · missing fields
      </div>
    );
  }

  // Merge attacker base + overrides into the live spec used for every row.
  const liveAtkStage = attackerOv.atk_stage ?? data.atk_stage ?? 0;
  const liveSpaStage = attackerOv.spa_stage ?? data.spa_stage ?? 0;
  const attackerSpec = {
    ...baseAttacker,
    nature: attackerOv.nature ?? baseAttacker.nature,
    evs: { ...baseAttacker.evs, ...(attackerOv.evs ?? {}) },
    boosts: {
      ...(data.attacker.boosts ?? {}),
      ...(liveAtkStage !== 0 ? { atk: liveAtkStage } : {}),
      ...(liveSpaStage !== 0 ? { spa: liveSpaStage } : {}),
    },
  };
  const attackerModified =
    Object.keys(attackerOv).length > 0 &&
    (attackerOv.nature !== undefined ||
      attackerOv.evs !== undefined ||
      attackerOv.atk_stage !== undefined ||
      attackerOv.spa_stage !== undefined);

  // Recompute each row when its defender override is set OR when the attacker
  // override is set (because the attacker change touches every row).
  const computed = data.results.map((row) => {
    const dov = defenderOv[row.defender.species];
    const rowModified = !!dov && Object.keys(dov).length > 0;
    if (!rowModified && !attackerModified) {
      return { row, defenderOv: null as DefenderOverride | null, rowModified: false };
    }
    return { row, defenderOv: dov ?? null, rowModified };
  });
  const sorted = [...computed].sort((a, b) => {
    const am = getDisplayMaxPct(a, attackerSpec, data.move, attackerModified) ?? -1;
    const bm = getDisplayMaxPct(b, attackerSpec, data.move, attackerModified) ?? -1;
    return bm - am;
  });

  return (
    <div className="vb-card">
      <div
        className="vb-card-header"
        style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Sprite species={data.attacker.species as SpeciesId} size={28} />
          <span
            className="mono"
            style={{ fontSize: 11, color: 'var(--fg-1)', whiteSpace: 'nowrap' }}
          >
            {data.move}
          </span>
          <span style={{ color: 'var(--fg-4)' }}>→</span>
          <span
            className="caps mono"
            style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.08em' }}
          >
            {sorted.length} defenders
          </span>
          {attackerModified && (
            <button
              type="button"
              className="mono caps"
              onClick={() => setAttackerOv({})}
              style={{
                marginLeft: 'auto',
                fontSize: 9,
                padding: '2px 6px',
                background: 'transparent',
                color: 'var(--accent-1)',
                border: '1px solid var(--accent-1)',
                borderRadius: 2,
                cursor: 'pointer',
                letterSpacing: '0.08em',
              }}
            >
              reset attacker
            </button>
          )}
        </div>
        <AttackerControls
          species={data.attacker.species}
          baseNature={baseAttacker.nature}
          override={attackerOv}
          serverAtkStage={data.atk_stage ?? 0}
          serverSpaStage={data.spa_stage ?? 0}
          baseAtkEv={baseAttacker.evs.atk ?? 0}
          baseSpaEv={baseAttacker.evs.spa ?? 0}
          onChange={(patch) => setAttackerOv((prev) => ({ ...prev, ...patch }))}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {sorted.map(({ row, defenderOv: dov, rowModified }) => (
          <MatrixRow
            key={row.defender.species}
            row={row}
            override={dov}
            rowModified={rowModified}
            attackerModified={attackerModified}
            attackerSpec={attackerSpec}
            move={data.move}
            onChange={(patch) =>
              setDefenderOv((prev) => ({
                ...prev,
                [row.defender.species]: { ...(prev[row.defender.species] ?? {}), ...patch },
              }))
            }
            onReset={() =>
              setDefenderOv((prev) => {
                const { [row.defender.species]: _, ...rest } = prev;
                return rest;
              })
            }
          />
        ))}
      </div>
    </div>
  );
}

function AttackerControls({
  species,
  baseNature,
  override,
  serverAtkStage,
  serverSpaStage,
  baseAtkEv,
  baseSpaEv,
  onChange,
}: {
  species: string;
  baseNature: string;
  override: AttackerOverride;
  serverAtkStage: number;
  serverSpaStage: number;
  baseAtkEv: number;
  baseSpaEv: number;
  onChange: (patch: AttackerOverride) => void;
}) {
  const nature = override.nature ?? baseNature;
  const atkEv = override.evs?.atk ?? baseAtkEv;
  const spaEv = override.evs?.spa ?? baseSpaEv;
  const atkStage = override.atk_stage ?? serverAtkStage;
  const spaStage = override.spa_stage ?? serverSpaStage;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        paddingTop: 4,
        borderTop: '1px solid var(--line-1)',
      }}
    >
      <span
        className="caps mono"
        style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.08em' }}
      >
        {prettySpecies(species)}
      </span>
      <NatureDropdown
        value={nature}
        options={NATURE_ALL}
        defaultValue={baseNature}
        onChange={(v) => onChange({ nature: v === baseNature ? undefined : v })}
      />
      <EvInput
        label="Atk"
        value={atkEv}
        defaultValue={baseAtkEv}
        onChange={(v) =>
          onChange({
            evs: { ...(override.evs ?? {}), atk: v === baseAtkEv ? undefined : v },
          })
        }
      />
      <EvInput
        label="SpA"
        value={spaEv}
        defaultValue={baseSpaEv}
        onChange={(v) =>
          onChange({
            evs: { ...(override.evs ?? {}), spa: v === baseSpaEv ? undefined : v },
          })
        }
      />
      <StageInput
        label="Atk"
        value={atkStage}
        onChange={(v) => onChange({ atk_stage: v === serverAtkStage ? undefined : v })}
      />
      <StageInput
        label="SpA"
        value={spaStage}
        onChange={(v) => onChange({ spa_stage: v === serverSpaStage ? undefined : v })}
      />
    </div>
  );
}

function MatrixRow({
  row,
  override,
  rowModified,
  attackerModified,
  attackerSpec,
  move,
  onChange,
  onReset,
}: {
  row: DamageMatrixRow;
  override: DefenderOverride | null;
  rowModified: boolean;
  attackerModified: boolean;
  attackerSpec: any;
  move: string;
  onChange: (patch: Partial<DefenderOverride>) => void;
  onReset: () => void;
}) {
  const computed = useMemo(() => {
    if (!rowModified && !attackerModified) return null;
    if (!row.ok || !row.defender) return null;
    return recomputeRow(row, override ?? {}, attackerSpec, move);
  }, [rowModified, attackerModified, row, override, attackerSpec, move]);

  const display: DamageMatrixRow = computed ?? row;

  if (!display.ok || !display.damage) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 12px',
          borderTop: '1px solid var(--line-1)',
        }}
      >
        <Sprite species={display.defender.species as SpeciesId} size={22} />
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-1)' }}>
          {prettySpecies(display.defender.species)}
        </span>
        <span
          className="mono caps"
          style={{
            fontSize: 9,
            color: 'var(--fg-4)',
            letterSpacing: '0.08em',
            marginLeft: 'auto',
          }}
        >
          {display.error ?? 'no result'}
        </span>
      </div>
    );
  }

  const variantStyle = variantStyles(display.variant ?? 'standard');
  const max = display.damage.maxPct;
  const min = display.damage.minPct;
  const koTextColor = display.isOhko ? 'var(--neg)' : max >= 50 ? 'var(--warn)' : 'var(--fg-2)';

  // The values currently in the dropdowns/inputs. Server defaults come from
  // row.defender; user overrides win.
  const defaultNature = (row.defender.nature as string) ?? 'Hardy';
  const defaultItem = row.defender.item;
  const defaultEvs: EvSet = (row.defender.evs as EvSet) ?? {};
  const currentNature = override?.nature ?? defaultNature;
  const currentItem = override?.item === null ? null : (override?.item ?? defaultItem);
  const currentHp = override?.evs?.hp ?? defaultEvs.hp ?? 0;
  const currentDef = override?.evs?.def ?? defaultEvs.def ?? 0;
  const currentSpd = override?.evs?.spd ?? defaultEvs.spd ?? 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '8px 12px',
        borderTop: '1px solid var(--line-1)',
        borderLeft: `2px solid ${variantStyle.accent}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Sprite species={display.defender.species as SpeciesId} size={22} />
        <span
          className="mono"
          style={{
            fontSize: 11,
            color: 'var(--fg-1)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {prettySpecies(display.defender.species)}
        </span>
        {row.filled_from_pikalytics && !rowModified && (
          <span
            className="mono caps"
            style={{
              fontSize: 8,
              color: 'var(--fg-4)',
              letterSpacing: '0.08em',
              padding: '0 4px',
              border: '1px solid var(--line-1)',
              borderRadius: 2,
            }}
            title="Item / ability / nature / EVs filled from Pikalytics modal"
          >
            modal
          </span>
        )}
        {rowModified && (
          <span
            className="mono caps"
            style={{
              fontSize: 8,
              color: 'var(--accent-1)',
              letterSpacing: '0.08em',
              padding: '0 4px',
              border: '1px solid var(--accent-1)',
              borderRadius: 2,
            }}
            title="Defender spec overridden from modal — recomputed client-side"
          >
            edited
          </span>
        )}
        <span
          className="mono tabular"
          style={{ fontSize: 12, color: 'var(--fg-1)', marginLeft: 'auto' }}
        >
          {min.toFixed(1)} – {max.toFixed(1)}%
        </span>
        {display.koChance?.text && (
          <span
            className="mono caps"
            style={{
              fontSize: 9,
              color: koTextColor,
              padding: '1px 6px',
              border: `1px solid ${koTextColor === 'var(--neg)' ? 'var(--neg)' : 'var(--line-2)'}`,
              borderRadius: 2,
              letterSpacing: '0.08em',
            }}
          >
            {display.koChance.text}
          </span>
        )}
      </div>
      {display.variant !== 'immune' && (
        <div className="damage-band" style={{ height: 4 }}>
          <div
            className="fill"
            style={{
              left: `${min}%`,
              width: `${Math.max(0, max - min)}%`,
            }}
          />
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <NatureDropdown
          value={currentNature}
          options={NATURE_DEFENDER}
          defaultValue={defaultNature}
          onChange={(v) => onChange({ nature: v === defaultNature ? undefined : v })}
        />
        <ItemDropdown
          value={currentItem}
          defaultValue={defaultItem}
          onChange={(v) => onChange({ item: v === defaultItem ? undefined : v })}
        />
        <EvInput
          label="HP"
          value={currentHp}
          defaultValue={defaultEvs.hp ?? 0}
          onChange={(v) =>
            onChange({
              evs: { ...(override?.evs ?? {}), hp: v === (defaultEvs.hp ?? 0) ? undefined : v },
            })
          }
        />
        <EvInput
          label="Def"
          value={currentDef}
          defaultValue={defaultEvs.def ?? 0}
          onChange={(v) =>
            onChange({
              evs: { ...(override?.evs ?? {}), def: v === (defaultEvs.def ?? 0) ? undefined : v },
            })
          }
        />
        <EvInput
          label="SpD"
          value={currentSpd}
          defaultValue={defaultEvs.spd ?? 0}
          onChange={(v) =>
            onChange({
              evs: { ...(override?.evs ?? {}), spd: v === (defaultEvs.spd ?? 0) ? undefined : v },
            })
          }
        />
        {rowModified && (
          <button
            type="button"
            className="mono caps"
            onClick={onReset}
            style={{
              fontSize: 9,
              padding: '2px 6px',
              background: 'transparent',
              color: 'var(--fg-4)',
              border: '1px solid var(--line-1)',
              borderRadius: 2,
              cursor: 'pointer',
              letterSpacing: '0.08em',
            }}
          >
            reset
          </button>
        )}
      </div>
    </div>
  );
}

function NatureDropdown({
  value,
  options,
  defaultValue,
  onChange,
}: {
  value: string;
  options: readonly string[];
  defaultValue: string;
  onChange: (v: string) => void;
}) {
  // Build the visible option list: canonical natures, plus the default value
  // if it's outside that list (rare modal pick like Quirky).
  const all = options.includes(value as any) ? options : ([...options, value] as readonly string[]);
  return (
    <label
      className="mono"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: 'var(--fg-3)',
      }}
    >
      <span style={{ letterSpacing: '0.06em' }}>{NATURE_LABEL.short}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mono"
        style={selectStyle}
      >
        {all.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
            {opt === defaultValue ? ' (default)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

function ItemDropdown({
  value,
  defaultValue,
  onChange,
}: {
  value: string | null | undefined;
  defaultValue: string | undefined;
  onChange: (v: string | null | undefined) => void;
}) {
  const displayCurrent = value === null ? NO_ITEM : prettyItem(value);
  const defaultDisplay = defaultValue === undefined ? NO_ITEM : prettyItem(defaultValue);
  // Option set: (none) + canonical items + the default item if outside that list.
  const opts = new Set<string>([NO_ITEM, ...ITEM_CANONICAL]);
  if (defaultValue !== undefined) opts.add(prettyItem(defaultValue));
  if (value && value !== null) opts.add(prettyItem(value));
  return (
    <label
      className="mono"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: 'var(--fg-3)',
      }}
    >
      <span style={{ letterSpacing: '0.06em' }}>item</span>
      <select
        value={displayCurrent}
        onChange={(e) => {
          const picked = e.target.value;
          if (picked === NO_ITEM) onChange(null);
          else onChange(normalizeItem(picked));
        }}
        className="mono"
        style={selectStyle}
      >
        {[...opts].map((opt) => (
          <option key={opt} value={opt}>
            {opt}
            {opt === defaultDisplay ? ' (default)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

function EvInput({
  label,
  value,
  defaultValue,
  onChange,
}: {
  label: string;
  value: number;
  defaultValue: number;
  onChange: (v: number) => void;
}) {
  const isDefault = value === defaultValue;
  return (
    <label
      className="mono"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: 'var(--fg-3)',
      }}
      title={isDefault ? `${label} EVs (default ${defaultValue})` : `${label} EVs`}
    >
      <span style={{ letterSpacing: '0.06em' }}>{label}</span>
      <input
        type="number"
        min={0}
        max={252}
        step={4}
        value={value}
        onChange={(e) => {
          const n = Number.parseInt(e.target.value, 10);
          if (Number.isFinite(n)) onChange(Math.max(0, Math.min(252, n)));
        }}
        className="mono tabular"
        style={{
          width: 48,
          fontSize: 10,
          padding: '1px 4px',
          background: 'var(--bg-2)',
          color: isDefault ? 'var(--fg-3)' : 'var(--accent-1)',
          border: '1px solid var(--line-1)',
          borderRadius: 2,
        }}
      />
    </label>
  );
}

function StageInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  const isZero = value === 0;
  return (
    <span
      className="mono"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        fontSize: 10,
        color: 'var(--fg-3)',
      }}
    >
      <span style={{ letterSpacing: '0.06em', marginRight: 2 }}>{label}</span>
      <button
        type="button"
        onClick={() => onChange(Math.max(-6, value - 1))}
        className="mono"
        style={stageButtonStyle}
        aria-label={`Decrease ${label} stage`}
      >
        −
      </button>
      <span
        className="mono tabular"
        style={{
          minWidth: 18,
          textAlign: 'center',
          color: isZero ? 'var(--fg-3)' : 'var(--accent-1)',
        }}
      >
        {value > 0 ? `+${value}` : value}
      </span>
      <button
        type="button"
        onClick={() => onChange(Math.min(6, value + 1))}
        className="mono"
        style={stageButtonStyle}
        aria-label={`Increase ${label} stage`}
      >
        +
      </button>
    </span>
  );
}

const selectStyle: React.CSSProperties = {
  fontSize: 10,
  padding: '1px 4px',
  background: 'var(--bg-2)',
  color: 'var(--fg-1)',
  border: '1px solid var(--line-1)',
  borderRadius: 2,
};

const stageButtonStyle: React.CSSProperties = {
  fontSize: 11,
  width: 18,
  height: 18,
  padding: 0,
  background: 'var(--bg-2)',
  color: 'var(--fg-2)',
  border: '1px solid var(--line-1)',
  borderRadius: 2,
  cursor: 'pointer',
  lineHeight: '14px',
};

// Recompute one row with current attacker + defender overrides. Falls back to
// the server's row on any throw so a typo or @pkmn/dex miss can't blank it.
function recomputeRow(
  row: DamageMatrixRow,
  override: DefenderOverride,
  attackerSpec: any,
  move: string,
): DamageMatrixRow {
  const baseEvs: EvSet = (row.defender.evs as EvSet) ?? {};
  const item =
    override.item === null
      ? undefined
      : override.item !== undefined
        ? override.item
        : row.defender.item;
  const def: any = {
    species: stripMega(row.defender.species),
    item,
    ability: row.defender.ability,
    nature: override.nature ?? row.defender.nature,
    evs: { ...baseEvs, ...(override.evs ?? {}) },
    ivs: row.defender.ivs,
    level: 50,
  };
  try {
    const { raw: _raw, ...rest } = calcDamage(
      { ...attackerSpec, species: stripMega(attackerSpec.species) },
      def,
      move,
    );
    const maxPct = rest.damage.maxPct;
    const variant: 'standard' | 'super' | 'resisted' | 'immune' =
      maxPct === 0 ? 'immune' : maxPct >= 75 ? 'super' : maxPct <= 20 ? 'resisted' : 'standard';
    return {
      ...row,
      ...rest,
      variant,
      defender: { ...row.defender, nature: def.nature, item: def.item, evs: def.evs },
    };
  } catch {
    return row;
  }
}

function stripMega(id: string): string {
  return id.toLowerCase().replace(/-mega(-[xy])?$/i, '');
}

function variantStyles(v: 'standard' | 'super' | 'resisted' | 'immune'): { accent: string } {
  switch (v) {
    case 'super':
      return { accent: 'var(--neg)' };
    case 'resisted':
      return { accent: 'var(--fg-4)' };
    case 'immune':
      return { accent: 'var(--fg-4)' };
    default:
      return { accent: 'var(--accent-1)' };
  }
}

function prettySpecies(id: string): string {
  return id
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

function getDisplayMaxPct(
  entry: { row: DamageMatrixRow; defenderOv: DefenderOverride | null; rowModified: boolean },
  attackerSpec: any,
  move: string,
  attackerModified: boolean,
): number | undefined {
  if (!entry.row.ok) return undefined;
  if (!entry.rowModified && !attackerModified) return entry.row.damage?.maxPct;
  const recomputed = recomputeRow(entry.row, entry.defenderOv ?? {}, attackerSpec, move);
  return recomputed.damage?.maxPct;
}
